/**
 * Sign in.
 *
 * The page carries the organisation's own identity — its name, its colours, its
 * words — because the branding endpoint answers before anybody has signed in.
 * Nothing on this page is written into a component: change the tenant
 * configuration and this page changes with it.
 *
 * The demonstration credentials panel is served by the API and only when the
 * environment permits it. It is not a hard-coded list in the client, and it
 * cannot appear in a production deployment.
 *
 * SELECTING AN ACCOUNT SIGNS YOU IN
 * ---------------------------------
 * It used to fill the form and wait, while announcing itself to a screen reader
 * as "Sign in as Joyce Okonkwo" — a control that said one thing and did
 * another. It now does what it says. A demonstration changes role eight or nine
 * times in ninety minutes, and every one of those was two clicks and a pause.
 */

import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import { useTenant } from '../lib/tenant';
import { useAsync, useMutation } from '../lib/useAsync';
import { ErrorNotice, Loading, TextInput } from '../components/ui';

interface DemoUser {
  username: string;
  fullName: string;
  jobTitle: string | null;
  roles: string;
  entities: string;
}

export function LoginPage() {
  const { signIn } = useSession();
  const { branding, environmentLabel, isDemonstration } = useTenant();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [choosing, setChoosing] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  /*
   * One mutation for both ways in — the form and the demonstration accounts —
   * so a failure is reported the same way whichever was used. Taking the
   * credentials as arguments rather than reading them back out of state matters
   * for the one-click path: the state set a moment earlier has not been applied
   * yet when the sign-in runs.
   */
  const attempt = useMutation(async (creds?: { username: string; password: string }) => {
    await signIn(creds ? creds.username : username.trim(), creds ? creds.password : password);
  });

  const demo = useAsync(
    () =>
      api.get<{
        available: boolean;
        users: DemoUser[];
        password?: string | null;
        primaryUsername?: string | null;
        notice?: string;
      }>(
        '/auth/demo-users',
      ),
    [],
  );

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void attempt.run(undefined);
  };

  /*
   * The one-click way in for somebody who has been handed the link and only
   * wants to see the thing. The account it uses is named by the API, not here:
   * the client has no business knowing whose account a demonstration opens on.
   */
  const primary = demo.data?.available
    ? (demo.data.users.find((u) => u.username === demo.data?.primaryUsername) ?? demo.data.users[0])
    : undefined;
  const canDemoSignIn = Boolean(primary && demo.data?.password);

  /** Choosing a demonstration account signs straight in with it. */
  const useAccount = async (user: DemoUser) => {
    const shared = demo.data?.password;
    setUsername(user.username);
    if (shared) setPassword(shared);
    if (!shared) return; // No shared password published: fall back to filling the form.
    setChoosing(user.username);
    try {
      // Through the mutation, so a refusal — a locked account, a rate limit —
      // appears in the error panel rather than the button simply doing nothing.
      await attempt.run({ username: user.username, password: shared });
    } finally {
      setChoosing(null);
    }
  };

  const login = branding.login;
  const provider = branding.providerLine ?? 'Powered by Altari Systems';

  return (
    <div className="login-page">
      <section className="login-hero">
        <div className="login-hero__inner">
          <div className="app-brand">
            <span className="app-brand__mark" aria-hidden="true">
              {branding.monogram}
            </span>
            <span className="app-brand__text">
              <span className="app-brand__name">{login?.heading ?? branding.productName}</span>
              <span className="app-brand__sub">{branding.productTagline}</span>
            </span>
          </div>

          {isDemonstration ? <p className="login-hero__environment">{environmentLabel}</p> : null}

          {login?.intro ? <p className="login-hero__intro">{login.intro}</p> : null}

          {login?.points?.length ? (
            <ul className="login-hero__points">
              {login.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          ) : null}

          <p className="login-hero__provider">{provider}</p>
        </div>
      </section>

      <section className="login-panel">
        <div className="login-form">
          <h1>Sign in</h1>
          {isDemonstration ? (
            <p className="smaller muted login-form__notice">
              All data in this environment is synthetic. It is not {branding.organisationName} data, and{' '}
              {branding.organisationName} has not adopted, approved or evaluated this software.
            </p>
          ) : null}

          <form onSubmit={onSubmit} className="stack stack--sm">
            <TextInput
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              autoComplete="username"
              autoFocus
              required
            />
            <TextInput
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              autoComplete="current-password"
              required
            />
            <ErrorNotice error={attempt.error} />
            <button type="submit" className="btn btn--primary btn--block" disabled={attempt.busy || choosing !== null}>
              {attempt.busy ? 'Signing in…' : 'Sign in'}
            </button>
            {canDemoSignIn && primary ? (
              <button
                type="button"
                className="btn btn--block login-form__demo"
                disabled={attempt.busy || choosing !== null}
                aria-label={`Sign in to the demonstration as ${primary.fullName}, ${primary.jobTitle ?? primary.roles}`}
                onClick={() => void useAccount(primary)}
              >
                {choosing === primary.username ? 'Signing in…' : 'Demo login'}
              </button>
            ) : null}
          </form>

          {demo.loading ? <Loading label="Checking for demonstration accounts" /> : null}

          {demo.data?.available ? (
            <div className="login-demo">
              <div className="login-demo__head">
                <h2 className="login-demo__title">Demonstration accounts</h2>
                <p className="smaller muted">
                  Choose a role to sign in. These accounts exist only in this environment.
                </p>
              </div>

              <ul className="login-demo-users">
                {demo.data.users.map((u) => (
                  <li key={u.username}>
                    <button
                      type="button"
                      className="demo-account"
                      disabled={choosing !== null || attempt.busy}
                      aria-label={`Sign in as ${u.fullName}, ${u.jobTitle ?? u.roles}`}
                      onClick={() => void useAccount(u)}
                    >
                      <span className="demo-account__name">{u.fullName}</span>
                      <span className="demo-account__role">{u.jobTitle ?? u.roles}</span>
                      {choosing === u.username ? <span className="demo-account__state">Signing in…</span> : null}
                    </button>
                  </li>
                ))}
              </ul>

              {/*
                The shared password is real and documented, but it is a
                credential: it belongs behind a deliberate action rather than
                printed across the sign-in page of a system being projected in
                front of a room.
              */}
              {demo.data.password ? (
                <p className="smaller muted login-demo__password">
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setShowPassword((v) => !v)}>
                    {showPassword ? 'Hide the shared password' : 'Show the shared password'}
                  </button>
                  {showPassword ? (
                    <>
                      {' '}
                      <span className="mono">{demo.data.password}</span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          ) : demo.data && !demo.data.available ? (
            <p className="smaller muted" style={{ marginTop: '1rem' }}>
              {demo.data.notice}
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}
